// Text metrics. Split out of model.ts because measuring needs a canvas and model.ts stays pure.

import { createId, emptyBounds, expandBounds, type Bounds, type TextObject } from './model.ts';

export const TEXT_FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';
export const TEXT_LINE_HEIGHT = 1.28;
/** Board-space font size for a pen size, so the toolbar's size control drives both. */
export const TEXT_SIZE_FACTOR = 3;
/** Empty text still needs a caret-sized box so a fresh block is selectable. */
const MIN_TEXT_WIDTH_EM = 0.6;

export function textFontSize(penSize: number): number {
  return Math.round(penSize * TEXT_SIZE_FACTOR);
}

export function textFont(size: number): string {
  return Math.round(size) + 'px ' + TEXT_FONT_STACK;
}

export function textLines(text: string): string[] {
  return text.split('\n');
}

let measureCtx: CanvasRenderingContext2D | null = null;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureCtx) return measureCtx;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  measureCtx = canvas.getContext('2d');
  return measureCtx;
}

/** Width of one line in board units. Falls back to a ratio when no context exists. */
export function measureLineWidth(line: string, size: number): number {
  const ctx = getMeasureContext();
  if (!ctx) return line.length * size * 0.55;
  ctx.font = textFont(size);
  return ctx.measureText(line).width;
}

export function measureTextBounds(x: number, y: number, text: string, size: number): Bounds {
  const lines = textLines(text);
  const lineHeight = size * TEXT_LINE_HEIGHT;
  let widest = size * MIN_TEXT_WIDTH_EM;
  for (let i = 0; i < lines.length; i++) {
    const w = measureLineWidth(lines[i], size);
    if (w > widest) widest = w;
  }
  const b = emptyBounds();
  expandBounds(b, x, y);
  expandBounds(b, x + widest, y + lineHeight * lines.length);
  return b;
}

export function createTextObject(
  x: number,
  y: number,
  text: string,
  color: string,
  size: number,
): TextObject {
  return { id: createId('t'), x, y, color, size, text, bounds: measureTextBounds(x, y, text, size) };
}

/** New object with the same id and anchor but re-measured content. */
export function retypeTextObject(t: TextObject, text: string): TextObject {
  return {
    id: t.id,
    x: t.x,
    y: t.y,
    color: t.color,
    size: t.size,
    text,
    bounds: measureTextBounds(t.x, t.y, text, t.size),
  };
}
